import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { OrganizationLocationDto } from "../../../models/location/dto/location.dto";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";

export const GET_LOCATION_QUERY_KEY = (locationId: string) => ['location', locationId];

/**
 * Read one location.
 *
 * ⚠️ The endpoint is `organizations/:organizationId/locations/:locationId`, NOT
 * `locations/:locationId`. There is no `@Controller('locations')` in the API at
 * all — locations are served only under the organization prefix — so the old
 * path answered 404 (route miss) for *every* id, in every environment, since
 * the hook was written. `LocationPage` renders `<ErrorState>` on `isError`, so
 * the location edit page failed on every deep link and, after the mount
 * refetch, on the cache-warm path from the list too. Nothing reported it: the
 * dashboard captures failed MUTATIONS to Sentry (`App.tsx`'s `MutationCache`)
 * and has no `QueryCache.onError`, so a permanently failing QUERY is invisible.
 * Its three siblings in this folder (`useUpdateLocation`,
 * `useUploadLocationImage`, `useCreateLocation`) always used the org-scoped
 * path — the asymmetric-sibling tell.
 *
 * `enabled` states when the request can succeed, not merely which arguments are
 * non-null: `organizationId` resolves asynchronously (zustand, seeded by
 * `GET my-organizations`) and `locationId` comes from a hash query param that
 * is simply absent on `#/admin/locationDetails` with no `?location=`. Without
 * the gate that combination requested
 * `organizations/undefined/locations/null`.
 *
 * The query key stays keyed on `locationId` alone: location ids are uuids, and
 * `useGetLocations` seeds this exact key for each row it lists.
 */
export const useGetLocation = (locationId: string) => {
    const client = useApiClient();
    const { selectedOrganization } = useSelectedOrganization();
    const organizationId = selectedOrganization?.id;

    return useQuery({
        enabled: Boolean(organizationId) && Boolean(locationId),
        queryKey: GET_LOCATION_QUERY_KEY(locationId),
        queryFn: () => client.fetchApi<OrganizationLocationDto, {}>({ endpoint: `organizations/${organizationId}/locations/${locationId}`, method: 'GET' }),
    });
}
